import React, { useEffect, useMemo } from "react";
import Styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getWallets } from "../../../store/cabinet/action";

import CryptoIcon from "../../_UI/cryptoIcon";

const getNetworkLabel = (asset) => {
    const assetType = asset?.type?.toUpperCase();
    const networkCode = asset?.network?.code?.toUpperCase();

    if (assetType && assetType !== "NATIVE") return assetType;
    if (networkCode) return networkCode;

    return "";
};

const getBalanceAmount = (balance) => {
    if (
        balance?.amount_decimal !== undefined &&
        balance?.amount_decimal !== null &&
        balance?.amount_decimal !== ""
    ) {
        return balance.amount_decimal;
    }

    const rawAmount = Number(balance?.amount || 0);
    const decimals = Number(balance?.asset?.decimals || 0);

    if (!Number.isFinite(rawAmount) || !Number.isFinite(decimals)) {
        return 0;
    }

    if (!decimals) {
        return rawAmount;
    }

    return rawAmount / Math.pow(10, decimals);
};

const formatBalance = (value, precision = 6) => {
    const numberValue = Number(value);

    if (!Number.isFinite(numberValue)) return "0";

    return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: precision > 6 ? 6 : precision,
    }).format(numberValue);
};

const formatUsd = (value) => {
    const numberValue = Number(value);

    if (!Number.isFinite(numberValue)) return "$0.00";

    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(numberValue);
};

const formatChange = (value) => {
    const numberValue = Number(value);

    if (!Number.isFinite(numberValue)) return "0.00%";

    const sign = numberValue > 0 ? "+" : "";

    return `${sign}${numberValue.toFixed(2)}%`;
};

const getValueUsd = (balance) => {
    const amount = Number(getBalanceAmount(balance));
    const rate = Number(balance?.asset?.currency?.rate || 0);

    if (!Number.isFinite(amount) || !Number.isFinite(rate)) {
        return 0;
    }

    return amount * rate;
};

const getChange24h = (balance) => {
    return balance?.asset?.currency?.rate_change_24h ?? 0;
};

const CabinetWalletAssets = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const wallets = useSelector(store => store.cabinet.wallets);

    useEffect(() => {
        dispatch(getWallets());
    }, [dispatch]);

    const assetsList = useMemo(() => {
        const walletsList = wallets?.wallets || [];

        return walletsList.flatMap((wallet) => {
            return (wallet?.balances || []).map((balance) => {
                const asset = balance?.asset;
                const currency = asset?.currency;
                const code = currency?.code || "";
                const networkLabel = getNetworkLabel(asset);
                const balanceAmount = getBalanceAmount(balance);

                return {
                    id: `${asset?.id}-${asset?.network?.id}-${asset?.type}`,
                    code,
                    name: currency?.name || code,
                    networkLabel,
                    networkCode: asset?.network?.code || "",
                    assetType: asset?.type || "",
                    balance: balanceAmount,
                    precision: currency?.precision || asset?.decimals || 6,
                    valueUsd: getValueUsd(balance),
                    change24h: getChange24h(balance),
                };
            });
        });
    }, [wallets]);

    return (
        <div className={Styles.container}>
            <div className={`${Styles.contentWrap} ${Styles.cabinetCard}`}>
                <div className={Styles.title}>
                    {t("Ваши активы")}
                </div>

                <div className={Styles.assetsScroll}>
                    <div className={Styles.assetsTable}>
                        <div className={`${Styles.tableRow} ${Styles.tableHead}`}>
                            <div className={Styles.assetCol}>
                                {t("Актив")}
                            </div>

                            <div className={Styles.balanceCol}>
                                {t("Баланс")}
                            </div>

                            <div className={Styles.valueCol}>
                                {t("Стоимость USD")}
                            </div>

                            <div className={Styles.changeCol}>
                                {t("Изменение за 24 часа")}
                            </div>
                        </div>

                        {assetsList.length > 0 ? (
                            assetsList.map((asset) => {
                                const changeValue = Number(asset.change24h);
                                const isNegative = changeValue < 0;

                                return (
                                    <div className={Styles.tableRow} key={asset.id}>
                                        <div className={Styles.assetCol}>
                                            <div className={Styles.assetInfo}>
                                                <CryptoIcon
                                                    code={asset.code}
                                                    networkCode={asset.networkCode}
                                                    assetType={asset.assetType}
                                                    className={Styles.assetIcon}
                                                />

                                                <div className={Styles.assetText}>
                                                    <div className={Styles.assetName}>
                                                        {asset.code}
                                                    </div>

                                                    <div className={Styles.assetCode}>
                                                        {asset.networkLabel || asset.code}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className={Styles.balanceCol}>
                                            <span className={Styles.mobileLabel}>
                                                {t("Баланс")}
                                            </span>

                                            <span className={Styles.cellValue}>
                                                {formatBalance(asset.balance, asset.precision)}
                                            </span>
                                        </div>

                                        <div className={Styles.valueCol}>
                                            <span className={Styles.mobileLabel}>
                                                {t("Стоимость USD")}
                                            </span>

                                            <span className={Styles.cellValue}>
                                                {formatUsd(asset.valueUsd)}
                                            </span>
                                        </div>

                                        <div className={`${Styles.changeCol} ${isNegative ? Styles.changeNegative : Styles.changePositive}`}>
                                            <span className={Styles.mobileLabel}>
                                                {t("Изменение за 24 часа")}
                                            </span>

                                            <span className={Styles.cellValue}>
                                                {formatChange(asset.change24h)}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className={Styles.emptyText}>
                                {t("Активы пока не найдены")}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CabinetWalletAssets;